import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dig from 'object-dig';
import moment from 'moment';
import { toast } from 'react-toastify';

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PeopleIcon from '@material-ui/icons/People';
import SaveAlt from '@material-ui/icons/SaveAlt';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';

import Chip from '@material-ui/core/Chip';
import LinkMaterial from '@material-ui/core/Link';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';

import Paper from '~/components/Paper';
import Button from '~/components/Button';
import Table from '~/components/Table/Table';
import GridItem from '~/components/GridItem';
import GridContainer from '~/components/GridContainer';
import InputSearch from '~/components/Input/InputSearch';
import LinearProgress from '~/components/LinearProgress';
import tableStyles from '~/components/Table/Table/styles';
import InputControl from '~/components/Input/InputControl';
import TablePagination from '~/components/Table/TablePagination';
import InputSelectAsync from '~/components/Input/InputSelectAsync';

import { showPerson } from '~/config/routes';
import { URL } from '~/config/uri';
import Request from '~/providers/Request';

import toInt from '~/utils/Number/toInt';
import maskCpf from '~/utils/maskCpf';
import maskCellPhone from '~/utils/maskCellPhone';
import isPresent from '~/utils/isPresent';
import fetchCorporatePartner from '~/utils/fetchCorporatePartner';
import csvGenerator from '~/utils/csvGenerator';
import xlsxGenerator from '~/utils/xlsxGenerator';
import { packageOptions } from '~/config/constants';

const onlyDigits = (value) => String(isPresent(value) ? value : '').replace(/\D/g, '');

function formatCpf(value) {
  const digits = onlyDigits(value);

  return digits.length === 11 ? maskCpf(digits) : value || '';
}

function formatPhone(value) {
  const digits = onlyDigits(value);

  return digits.length === 10 || digits.length === 11
    ? maskCellPhone(digits)
    : value || '';
}

function formatCns(value) {
  const digits = onlyDigits(value);

  return digits.length === 15
    ? digits.replace(/(\d{3})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4')
    : value || '';
}

function formatDate(value) {
  return isPresent(value) ? moment(value).format('DD/MM/YYYY') : '';
}

function hasPack(row, packType) {
  return packType === 'doctor' ? !!row.has_doctor : !!row.has_doctor_all;
}

function StationReport() {
  const { t } = useTranslation();
  const classesTable = tableStyles();

  const [loading, setLoading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [corporation, setCorporation] = useState(null);

  const [inputSearch, setInputSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const trAttr = (attr) => t(`stationReport.attributes.${attr}`);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const corpsId = corporation ? `[${corporation.id}]` : null;

  async function generateFile(filters, type) {
    try {
      const registers = await Request.get(
        '/admin/report/station_users',
        filters,
        { normalizeJSON: false }
      );

      const customName = dig(corporation, 'name') || 'Empresa';

      const rows = [];
      rows.push([`${customName} - ${t('stationReport.title')}`.toUpperCase()]);
      rows.push(['']);
      rows.push(['Total de pacientes', registers.meta.total]);
      rows.push(['']);

      rows.push([
        'ID',
        'Nome',
        'CPF',
        'CNS',
        'Telefone',
        'E-mail',
        trAttr('complete_signup'),
        packageOptions.doctor,
        packageOptions.doctor_all,
        trAttr('created_at'),
      ]);

      registers.data.map((item) => {
        rows.push([
          item.id,
          item.name,
          formatCpf(item.cpf),
          formatCns(item.cns),
          formatPhone(item.cell_phone),
          item.email,
          isPresent(item.signup_messages) ? 'Não' : 'Sim',
          hasPack(item, 'doctor') ? 'Sim' : 'Não',
          hasPack(item, 'doctor_all') ? 'Sim' : 'Não',
          formatDate(item.created_at),
        ]);
        return item;
      });

      if (type === 'csv')
        csvGenerator(`${customName.toLocaleUpperCase()} - Pacientes.csv`, [
          ...rows,
        ]);
      else
        xlsxGenerator(`${customName.toLocaleUpperCase()} - Pacientes.xlsx`, [
          ...rows,
        ]);

      toast.success(
        `Arquivo ${type.toLocaleUpperCase()} de pacientes gerado com sucesso!`
      );
    } catch (error) {
      console.log(error);
      toast.error(
        `Não foi possível gerar o arquivo ${type.toLocaleUpperCase()} dos pacientes`
      );
    }
  }

  async function onExportPdf() {
    if (!corporation) {
      toast.warn('Selecione uma empresa para gerar o arquivo.');
      return;
    }

    setLoadingPdf(true);

    const toastId = toast.info(
      'Arquivo PDF está sendo gerado. Dependendo do volume de pacientes, pode levar alguns minutos.',
      { autoClose: false }
    );

    try {
      const params = `?qs=&partners=${encodeURIComponent(corpsId)}`;

      const res = await fetch(`${URL}/admin/report/station_users/pdf${params}`, {
        headers: await Request.getHeaders(),
      });

      if (res.status === 429) {
        const { error } = await res.json();

        toast.dismiss(toastId);
        toast.warn(error);
        setLoadingPdf(false);

        return;
      }

      if (!res.ok) throw new Error(res.statusText);

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `${dig(corporation, 'name') || 'Empresa'} - Pacientes.pdf`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.dismiss(toastId);
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.log(error);
      toast.dismiss(toastId);
      toast.error('Não foi possível gerar o PDF dos pacientes');
    }

    setLoadingPdf(false);
  }

  function onExport(type) {
    if (!corporation) {
      toast.warn('Selecione uma empresa para gerar o arquivo.');
      return;
    }

    toast.info(`Arquivo ${type.toLocaleUpperCase()} esta sendo gerado!`);

    generateFile(
      {
        qs: '',
        page: 1,
        per_page: 500000,
        partners: corpsId,
      },
      type
    );
  }

  async function getUsers() {
    setLoading(true);

    const filters = {
      qs: inputSearch,
      page: page + 1,
      per_page: rowsPerPage,
      partners: corpsId,
    };

    Request.get('/admin/report/station_users', filters, {
      normalizeJSON: false,
    })
      .then((response) => {
        const { data, meta } = response;
        setLoading(false);
        setUsers(data);
        setPage(toInt(meta.page) - 1);
        setTotalUsers(toInt(meta.total));
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }

  async function getFirstCorporation() {
    fetchCorporatePartner(null, 0, (data) => {
      setCorporation(data.items[0]);
    });
  }

  async function fetchParams() {
    const url = new URLSearchParams(window.location.search);
    const corporation_id = url.get('corporation_id');

    if (isPresent(corporation_id) && corporation_id !== 'null') {
      try {
        const { data } = await Request.get(
          `/admin/partner/${corporation_id}`,
          null,
          { normalizeJSON: true }
        );

        setCorporation(data);
      } catch (error) {
        console.log(error);
        toast.info(
          'Não foi possível encontrar a empresa informada por parâmetro'
        );
        getFirstCorporation();
      }
    } else {
      getFirstCorporation();
    }
  }

  useEffect(() => {
    setPage(0);
  }, [inputSearch, corporation]);

  useEffect(() => {
    if (corporation) getUsers();
  }, [inputSearch, page, rowsPerPage, corporation]);

  useEffect(() => {
    fetchParams();
  }, []);

  return (
    <>
      <Paper
        Icon={PeopleIcon}
        iconColor="blue"
        title={t('stationReport.title')}
      >
        <GridContainer>
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputControl
              name={'corporation'}
              label={trAttr('corporation')}
              value={dig(corporation, 'name')}
              inputComponent={InputSelectAsync}
              onChange={({ corporation: corp }) => {
                setCorporation(corp);
              }}
              InputProps={{
                isClearable: false,
                handleFetch: fetchCorporatePartner,
                createEnabled: false,
                error: false,
              }}
            />
          </GridItem>
          <GridItem
            xs={12}
            sm={6}
            md={6}
            lg={6}
            xl={6}
            style={{ textAlign: 'right', paddingRight: 16 }}
          >
            <Button color="red" disabled={loadingPdf} onClick={onExportPdf}>
              PDF <SaveAlt />
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              color="second"
              onClick={() => onExport('csv')}
            >
              {t('action.export_csv')} <SaveAlt />
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              color="orange"
              onClick={() => onExport('xlsx')}
            >
              {t('action.export_xlsx')} <SaveAlt />
            </Button>
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputSearch
              value={inputSearch}
              label={t('action.search')}
              placeholder={t('stationReport.extras.search')}
              onChange={setInputSearch}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <Table
          emptyData={users.length === 0}
          loading={loading}
          columns={
            <>
              <TableCell className={classesTable.cell}>
                {trAttr('id')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('name')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('cpf')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('cns')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('cell_phone')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('email')}
              </TableCell>
              <TableCell className={classesTable.cell} align="center">
                {trAttr('complete_signup')}
              </TableCell>
              <TableCell className={classesTable.cell} align="center">
                {packageOptions.doctor}
              </TableCell>
              <TableCell className={classesTable.cell} align="center">
                {packageOptions.doctor_all}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('created_at')}
              </TableCell>
            </>
          }
        >
          {users.map((row) => {
            return (
              <TableRow key={row.id} hover className={classesTable.row}>
                <TableCell>
                  <Chip
                    label={row.id}
                    component={LinkMaterial}
                    href={showPerson.reverse({ id: row.id })}
                    style={{ cursor: 'pointer' }}
                    target="_blank"
                    color="primary"
                    variant="outlined"
                    onDelete={() => {}}
                    deleteIcon={<OpenInNewIcon />}
                  />
                </TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{formatCpf(row.cpf)}</TableCell>
                <TableCell>{formatCns(row.cns)}</TableCell>
                <TableCell>{formatPhone(row.cell_phone)}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell align="center">
                  {isPresent(row.signup_messages) ? (
                    <CloseIcon color="error" fontSize="small" />
                  ) : (
                    <CheckIcon style={{ color: '#4caf50' }} fontSize="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  {hasPack(row, 'doctor') ? (
                    <CheckIcon style={{ color: '#4caf50' }} fontSize="small" />
                  ) : (
                    <CloseIcon color="error" fontSize="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  {hasPack(row, 'doctor_all') ? (
                    <CheckIcon style={{ color: '#4caf50' }} fontSize="small" />
                  ) : (
                    <CloseIcon color="error" fontSize="small" />
                  )}
                </TableCell>
                <TableCell>{formatDate(row.created_at)}</TableCell>
              </TableRow>
            );
          })}
        </Table>

        <LinearProgress loading={loading} />

        <Typography
          variant="subtitle2"
          color="primary"
          align="right"
          style={{ paddingRight: 16, marginTop: 10 }}
        >
          <strong>Total: </strong>
          {totalUsers}
        </Typography>

        <TablePagination
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
}

export default StationReport;
